import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Info, X } from 'lucide-react';
import { Button, Callout } from '@/shared/ui';
import { formatDate, formatTimestamp } from '@/shared/utils/format';
import { useTaskThread } from '../hooks/useTaskThread';
import { useTaskVersions } from '../hooks/useTaskVersions';
import {
  agentDisplayName,
  agentInitial,
  agentTone,
  type AgentIdentity,
  type AgentTone,
} from '../utils/agentLabels';
import {
  activityLabel,
  agentMessageText,
  conversationScript,
  decisionContext,
  isClosedTask,
  mentionedVersion,
  threadSuggestions,
  footerMode,
  threadEntryRole,
  replyQuoteText,
} from '../utils/conversationPilot';
import type { AgentTaskWithdrawalKind } from '../types';
import type { UnifiedTaskItem } from '../utils/unifiedTasks';
import { AgentChip } from './AgentChip';
import { DecisionTargetProvider } from './DecisionSlot';
import { TaskWorkspace } from './TaskWorkspace';
import styles from './ConversationWorkspace.module.css';

const AVATAR_TONE: Record<AgentTone, string> = {
  accent: styles.toneAccent,
  info: styles.toneInfo,
  purple: styles.tonePurple,
  neutral: styles.toneNeutral,
};

/**
 * The conversation view (the default; flag `classic-agent-tasks` switches a practice back to the
 * tabbed view): one task as a chat with its agent.
 * Top to bottom: the brief, the agent's turn with the draft or report as a card inside it (the same
 * TaskWorkspace as the tabbed view), the thread, and the agent's closing question. Pinned below the
 * stream: the task's decision (Approve / Reject / Skip, Mark reviewed, publish status), suggested
 * replies and the message box, so a decision never scrolls away. Mounted with `key={task.id}`.
 */
export function ConversationWorkspace({
  item,
  agents,
  locationId,
  onBack,
  busy,
  reviewBusy,
  retryPublishBusy,
  onApprove,
  onReject,
  onReview,
  onRetryPublish,
  onSkip,
  onCancel,
  onWithdraw,
}: {
  item: UnifiedTaskItem;
  agents: readonly AgentIdentity[];
  locationId: string;
  onBack: () => void;
  busy: boolean;
  reviewBusy: boolean;
  retryPublishBusy: boolean;
  onApprove: (taskId: string) => void;
  onReject: (taskId: string, reason?: string) => void;
  onReview: (taskId: string) => void;
  onRetryPublish: (taskId: string) => void;
  onSkip: (taskId: string, reason?: string) => void;
  onCancel: (taskId: string, reason?: string) => void;
  /** Agency only: withdraw open work (absent for practice users). */
  onWithdraw?: (taskId: string, kind: AgentTaskWithdrawalKind, reason?: string) => void;
}) {
  const task = item.task;
  const agentName = agentDisplayName(item.agentType, agents);
  const script = conversationScript(task);
  const { messages, send, sending, retry, retrying, stop, stopping } = useTaskThread(
    task.id,
    locationId,
    true,
  );
  const [viewingVersion, setViewingVersion] = useState<number | null>(null);
  const [text, setText] = useState('');
  // The pinned decision bar's element; TaskWorkspace's (and Joe's) action bars portal into it.
  const [decisionTarget, setDecisionTarget] = useState<HTMLDivElement | null>(null);
  const streamRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const seenCount = useRef<number | null>(null);
  // "About <agent>": a side panel with the conversation's background, closed by default.
  const [aboutOpen, setAboutOpen] = useState(false);
  // Same query as the card's version bar (shared cache), for the panel's version list.
  const { versions, current } = useTaskVersions(task.id, locationId, true);
  const agentRole = agents.find((a) => a.roleKey === item.agentType)?.displayName;

  // Digests filed on this task but about all of the agent's work are not part of the conversation.
  const thread = messages.filter((m) => threadEntryRole(m) !== 'hidden');

  // Opening a conversation starts at the top (the brief and the card). Only a message that arrives
  // while it is open scrolls the stream to the newest one.
  useEffect(() => {
    const el = streamRef.current;
    if (seenCount.current !== null && thread.length > seenCount.current && el) {
      el.scrollTop = el.scrollHeight;
    }
    seenCount.current = thread.length;
  }, [thread.length]);

  const working = sending || !!task.replying;
  const closed = isClosedTask(task);
  const lastIsNotice = thread[thread.length - 1]?.kind === 'notice';
  const ownerSpoke = thread.some((m) => m.direction === 'inbound');
  const suggestions = !closed && !working && !lastIsNotice ? threadSuggestions(script, thread) : [];
  const time = task.lastAdvancedAt ? formatDate(new Date(task.lastAdvancedAt), 'MMM d') : '';
  const needsYou = item.stage === 'needs_attention';
  // While the agent answers the practice's question, the decision waits: the "Waiting on you" bar
  // comes back once the answer is in, so it never shows under "working on it…".
  const awaitingDecision = needsYou && !working;
  const decision = decisionContext(task, item.typeLabel);
  // "See draft" scrolls back to the card and marks it for a moment, so the eye lands on it.
  const [highlight, setHighlight] = useState(false);
  useEffect(() => {
    if (!highlight) return;
    const timer = window.setTimeout(() => setHighlight(false), 1600);
    return () => window.clearTimeout(timer);
  }, [highlight]);
  const showContext = () => {
    cardRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    setHighlight(true);
  };

  const submit = (body: string) => {
    const t = body.trim();
    if (!t || sending) return;
    send(t);
    setText('');
    setChanging(false);
  };
  // Enter sends; Shift+Enter inserts a newline (same as the tabbed view's thread).
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit(text);
    }
  };
  // While a decision is waiting, the footer shows only the decision. "Make changes" (or Joe's
  // "Request changes", or "Ask <agent>" on a report) swaps it for a change prompt: suggested
  // changes plus the message box. Sending, or "Back to decision", returns to the decision.
  const [changing, setChanging] = useState(false);
  const askForChanges = () => setChanging(true);
  // "Ask Jenny about this" on a report: the question goes in the message box, ready to send or edit.
  const askAbout = (question: string) => {
    setText(question);
    setChanging(true);
  };
  useEffect(() => {
    if (changing) inputRef.current?.focus();
  }, [changing]);
  // Whether a decision bar has portalled into the pinned slot (TaskWorkspace renders it).
  const [hasDecision, setHasDecision] = useState(false);
  useEffect(() => {
    if (!decisionTarget) return;
    const update = () => setHasDecision(decisionTarget.childElementCount > 0);
    update();
    const observer = new MutationObserver(update);
    observer.observe(decisionTarget, { childList: true });
    return () => observer.disconnect();
  }, [decisionTarget]);
  const deciding = awaitingDecision && hasDecision;
  const { showDecision, showPrompt, showChangesHead } = footerMode({
    deciding,
    changing,
    ownerSpoke,
  });
  const showVersion = (n: number) => {
    setViewingVersion(n);
    cardRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };

  return (
    <div className={styles.root}>
      <button type="button" className={styles.backButton} onClick={onBack}>
        <span aria-hidden>←</span>
        Back to tasks
      </button>
      <div className={styles.body}>
        <div className={styles.panel}>
          <header className={styles.header}>
            <Avatar agentType={item.agentType} name={agentName} />
            <div className={styles.headerText}>
              <h2 className={styles.title}>{item.title}</h2>
              <div className={styles.meta}>
                <AgentChip agentType={item.agentType} agents={agents} />
                {item.typeLabel !== item.title && (
                  <>
                    <span aria-hidden>·</span>
                    <span>{item.typeLabel}</span>
                  </>
                )}
              </div>
            </div>
            <span
              className={`${styles.status} ${
                needsYou
                  ? styles.statusAttention
                  : item.stage === 'done'
                    ? styles.statusDone
                    : styles.statusWorking
              }`}
            >
              {item.statusLabel}
            </span>
            <button
              type="button"
              className={
                aboutOpen ? `${styles.aboutButton} ${styles.aboutButtonOpen}` : styles.aboutButton
              }
              onClick={() => setAboutOpen((open) => !open)}
              aria-expanded={aboutOpen}
              aria-controls="conversation-about"
            >
              <Info size={14} aria-hidden />
              About {agentName}
            </button>
          </header>

          <div className={styles.stream} ref={streamRef}>
            <div className={styles.brief}>
              <span className={styles.briefLabel}>
                {item.objective
                  ? `What ${agentName} was asked to do`
                  : `${agentName} suggested this`}
              </span>
              <span>{item.objective ?? item.typeLabel}</span>
            </div>

            <AgentTurn agentType={item.agentType} name={agentName} time={time}>
              <p className={styles.text}>{script.lead}</p>
              <div
                className={highlight ? `${styles.card} ${styles.cardHighlight}` : styles.card}
                ref={cardRef}
              >
                <DecisionTargetProvider value={decisionTarget}>
                  <TaskWorkspace
                    task={task}
                    locationId={locationId}
                    busy={busy}
                    reviewBusy={reviewBusy}
                    onApprove={onApprove}
                    onReject={onReject}
                    onReview={onReview}
                    onRetryPublish={onRetryPublish}
                    retryPublishBusy={retryPublishBusy}
                    onSkip={onSkip}
                    onCancel={onCancel}
                    onWithdraw={onWithdraw}
                    agentName={agentName}
                    embedded
                    viewingVersion={viewingVersion}
                    onViewingVersionChange={setViewingVersion}
                    onAskForChanges={askForChanges}
                    onAsk={askAbout}
                  />
                </DecisionTargetProvider>
              </div>
              {!closed && !ownerSpoke && <p className={styles.text}>{script.prompt}</p>}
            </AgentTurn>

            {thread.map((m, index) => {
              const role = threadEntryRole(m);
              if (m.kind === 'notice') {
                // From the system, not the agent. Retry only on the latest message, so an older
                // notice never re-runs a turn already answered.
                return (
                  <Callout
                    key={m.id}
                    variant="warning"
                    actions={
                      index === thread.length - 1 ? (
                        <Button variant="secondary" disabled={retrying} onClick={() => retry()}>
                          Retry
                        </Button>
                      ) : undefined
                    }
                  >
                    {m.body}
                  </Callout>
                );
              }
              if (role === 'activity') {
                return (
                  <details key={m.id} className={styles.activity}>
                    <summary title={formatTimestamp(new Date(m.at))}>
                      {agentName} · {activityLabel(m.kind)}
                      {m.channel === 'email' ? ' by email' : ''} ·{' '}
                      {formatDate(new Date(m.at), 'MMM d, h:mm a')}
                    </summary>
                    <div className={styles.markdown}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.body}</ReactMarkdown>
                    </div>
                  </details>
                );
              }
              return m.direction === 'outbound' ? (
                <AgentTurn
                  key={m.id}
                  agentType={item.agentType}
                  name={agentName}
                  time={formatDate(new Date(m.at), 'MMM d, h:mm a')}
                  title={formatTimestamp(new Date(m.at))}
                >
                  {(() => {
                    // Several questions sent together are answered one at a time below them: each answer
                    // quotes the question it answers.
                    const quote = replyQuoteText(m, thread[index - 1], thread);
                    return quote ? (
                      <blockquote className={styles.replyQuote}>
                        <span className={styles.replyQuoteLabel}>You asked</span>
                        {quote}
                      </blockquote>
                    ) : null;
                  })()}
                  <div className={styles.markdown}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {agentMessageText(m.body, agentName)}
                    </ReactMarkdown>
                  </div>
                  {m.producedVersion != null ? (
                    <button
                      type="button"
                      className={styles.versionTag}
                      onClick={() => showVersion(m.producedVersion as number)}
                    >
                      Updated the draft · see v{m.producedVersion}
                    </button>
                  ) : (
                    // "Saved as version 14": the message names a draft it did not produce; link to it.
                    (() => {
                      const named = mentionedVersion(m.body, versions.length);
                      return named !== undefined ? (
                        <button
                          type="button"
                          className={styles.versionTag}
                          onClick={() => showVersion(named)}
                        >
                          See v{named}
                        </button>
                      ) : null;
                    })()
                  )}
                </AgentTurn>
              ) : (
                <div key={m.id} className={styles.userRow}>
                  {/* The practice's own messages carry their time too, like the agent's. */}
                  <div className={styles.userMeta} title={formatTimestamp(new Date(m.at))}>
                    You · {formatDate(new Date(m.at), 'MMM d, h:mm a')}
                  </div>
                  <div className={styles.userBubble} title={formatTimestamp(new Date(m.at))}>
                    <div className={styles.markdown}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.body}</ReactMarkdown>
                    </div>
                  </div>
                </div>
              );
            })}

            {working && (
              <div className={styles.typingRow} role="status">
                <Avatar agentType={item.agentType} name={agentName} small />
                <span className={styles.typing}>
                  <span className={styles.dots} aria-hidden>
                    <span />
                    <span />
                    <span />
                  </span>
                  {agentName} is working on it…
                </span>
                {/* Like any chat: stop an answer that has started. Nothing more is sent; it can be asked again. */}
                {task.replying && (
                  <Button variant="secondary" size="sm" disabled={stopping} onClick={() => stop()}>
                    {stopping ? 'Stopping…' : 'Stop'}
                  </Button>
                )}
              </div>
            )}
          </div>

          <div className={styles.footer}>
            {/* Hidden by CSS until a decision bar portals into the slot. */}
            {/* Stays mounted while the change prompt shows: it is the portal target. */}
            <div
              className={[
                styles.decision,
                awaitingDecision ? styles.decisionOpen : '',
                showDecision ? '' : styles.decisionHidden,
              ].join(' ')}
            >
              {awaitingDecision && (
                <div className={styles.decisionContext}>
                  <span className={styles.decisionLabel}>Waiting on you</span>
                  <span className={styles.decisionSubject}>
                    {decision.subject}
                    {decision.checks > 0 &&
                      ` · ${decision.checks} ${decision.checks === 1 ? 'thing' : 'things'} to confirm first`}
                  </span>
                  <button type="button" className={styles.contextLink} onClick={showContext}>
                    {task.bucket === 'audit' ? 'See report' : 'See draft'} ↑
                  </button>
                </div>
              )}
              <div className={styles.decisionSlot} ref={setDecisionTarget} />
            </div>
            {showChangesHead && (
              <div className={styles.changesHead}>
                <span className={styles.changesTitle}>
                  {task.bucket === 'audit'
                    ? `What would you like to ask ${agentName}?`
                    : `What should ${agentName} change?`}
                </span>
                <button
                  type="button"
                  className={styles.contextLink}
                  onClick={() => setChanging(false)}
                >
                  Back to decision
                </button>
              </div>
            )}
            {showPrompt && suggestions.length > 0 && (
              <div className={styles.suggestions} aria-label="Suggested replies">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={styles.suggestion}
                    onClick={() => submit(s)}
                  >
                    {s}
                    <span aria-hidden className={styles.suggestionArrow}>
                      →
                    </span>
                  </button>
                ))}
              </div>
            )}
            {showPrompt && (
              <div className={styles.composer}>
                <textarea
                  ref={inputRef}
                  className={styles.input}
                  rows={1}
                  value={text}
                  aria-label={`Message ${agentName}`}
                  placeholder={
                    changing
                      ? `Or describe it in your own words… (Enter to send)`
                      : `Reply to ${agentName}, or ask anything about this… (Enter to send)`
                  }
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={onKeyDown}
                />
                <Button
                  variant="primary"
                  size="sm"
                  disabled={sending || !text.trim()}
                  onClick={() => submit(text)}
                >
                  Send
                </Button>
              </div>
            )}
          </div>
        </div>
        {aboutOpen && (
          <aside id="conversation-about" className={styles.about} aria-label={`About ${agentName}`}>
            <div className={styles.aboutHead}>
              <span className={styles.aboutTitle}>About this conversation</span>
              <button
                type="button"
                className={styles.aboutClose}
                onClick={() => setAboutOpen(false)}
                aria-label="Close"
              >
                <X size={14} aria-hidden />
              </button>
            </div>
            <div className={styles.aboutBody}>
              <div className={styles.aboutAgent}>
                <Avatar agentType={item.agentType} name={agentName} />
                <div>
                  <div className={styles.aboutAgentName}>{agentName}</div>
                  {agentRole && <div className={styles.aboutMuted}>{agentRole}</div>}
                </div>
              </div>
              <section className={styles.aboutSection}>
                <h3 className={styles.aboutLabel}>How this started</h3>
                <div className={styles.aboutStrong}>
                  {item.objective ? `${agentName} was asked to` : `${agentName} suggested this`}
                </div>
                <div>{item.objective ?? item.typeLabel}</div>
                {time && <div className={styles.aboutMuted}>Last updated {time}</div>}
              </section>
              <section className={styles.aboutSection}>
                <h3 className={styles.aboutLabel}>Type of work</h3>
                <div>{item.typeLabel}</div>
              </section>
              <section className={styles.aboutSection}>
                <h3 className={styles.aboutLabel}>Status</h3>
                <div>{item.statusLabel}</div>
              </section>
              {versions.length > 0 && (
                <section className={styles.aboutSection}>
                  <h3 className={styles.aboutLabel}>Versions</h3>
                  {versions.map((v) => (
                    <button
                      key={v.n}
                      type="button"
                      className={styles.aboutVersion}
                      onClick={() => showVersion(v.n)}
                    >
                      <span className={styles.aboutVersionN}>v{v.n}</span>
                      <span>
                        {v.n === current ? 'Current' : formatDate(new Date(v.createdAt), 'MMM d')}
                        {v.n === current && needsYou ? ' · waiting on you' : ''}
                      </span>
                    </button>
                  ))}
                </section>
              )}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

function Avatar({
  agentType,
  name,
  small = false,
}: {
  agentType: string | null;
  name: string;
  small?: boolean;
}) {
  return (
    <span
      className={`${styles.avatar} ${small ? styles.avatarSm : ''} ${AVATAR_TONE[agentTone(agentType)]}`}
      aria-hidden
    >
      {agentInitial(name)}
    </span>
  );
}

function AgentTurn({
  agentType,
  name,
  time,
  title,
  children,
}: {
  agentType: string | null;
  name: string;
  time: string;
  title?: string;
  children: ReactNode;
}) {
  return (
    <div className={styles.agentRow}>
      <Avatar agentType={agentType} name={name} small />
      <div className={styles.agentBody}>
        <div className={styles.agentMeta} title={title}>
          <strong className={styles.agentName}>{name}</strong>
          {time ? ` · ${time}` : ''}
        </div>
        {children}
      </div>
    </div>
  );
}
